import { Box, Grid, Typography } from '@atgfr8/atg-ui-tailwind';
import { calculateMargin } from '@common/utils';
import { Popover } from '@components';
import {
  FalveyInsuranceQuote,
  LtlQuoteParametersViewModel,
  LtlQuoteViewModel,
} from '@store/orders/types';
import { InformationOutlineIcon } from '@atgfr8/icons';
import { formatAmount } from '@utils/utils';
import { Show } from 'solid-js';

import { ViewCostDetailsPopover } from '../../ViewCostDetailsPopover';
import { quoteDetailsRowRenderClasses as cls } from '../classes';
import { QuoteActionDetailsLegacy } from './QuoteActionDetailsLegacy';

export const QuoteCostDetailsLegacy = (props: {
  quoteParameters: LtlQuoteParametersViewModel;
  quote: LtlQuoteViewModel;
  insuranceQuote?: FalveyInsuranceQuote;
  customerId: number;
  isHistory: boolean;
  isInsuranceRequest: boolean;
  testMode: boolean;
}) => {
  const getTotalPrice = (
    isInsuranceRequest: boolean,
    carrierTotal: number,
    insurancePremium: number,
  ) => {
    const totalCost = isInsuranceRequest
      ? insurancePremium + carrierTotal
      : carrierTotal;
    return formatAmount(totalCost);
  };

  const margin = calculateMargin(
    props.quote.customerTotal ?? 0,
    props.quote.carrierTotal,
  );

  return (
    <Box class={cls.container} p={0.5}>
      <Box class="flex flex-col justify-between h-full w-fill-available!">
        <Grid container spacing={0.5} alignItems="stretch">
          {/* Cost Details Popover & Margin */}
          <Grid
            item
            size={{ xs: 12, sm: 6 }}
            class="small-netbook:w-1/2!"
            paddingLeft={'0px !important'}
          >
            <Box class={cls.popoverContainer}>
              <Popover
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                eleName={
                  <button class={cls.popoverButton}>
                    <InformationOutlineIcon class="mr-1" />
                    <span class="text-wrap!">Cost Details</span>
                  </button>
                }
                classes="p-0!"
              >
                <ViewCostDetailsPopover
                  quote={props.quote}
                  insuranceQuote={props.insuranceQuote}
                />
              </Popover>
            </Box>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }} class="small-netbook:w-1/2!">
            <Show when={margin > 0}>
              <Typography variant="body2" align="right" class="text-wrap">
                Margin: {margin}%
              </Typography>
            </Show>
          </Grid>
          {/* Row 2: Agent Cost (Bold) */}
          <Grid item size={6}>
            <Typography variant="body2" class={cls.boldText}>
              Agent
            </Typography>
          </Grid>
          <Grid item size={6}>
            <Typography
              variant="body1"
              align="right"
              data-testid="quoteCostDetails-carrierTotal-quote-{props.quote.id}"
            >
              {getTotalPrice(
                props.isInsuranceRequest,
                props.quote.carrierTotal,
                props.insuranceQuote?.premium ?? 0,
              )}
            </Typography>
          </Grid>
          {/* Row 3: Carrier Rate (Indented) */}
          <Grid item size={6}>
            <Box class={cls.indentedLabel}>
              <Typography
                class={`${cls.tightSpacing} text-wrap!`}
                variant="body2"
              >
                Carrier Rate
              </Typography>
            </Box>
          </Grid>
          <Grid item size={6}>
            <Typography class="text-wrap!" variant="body2" align="right">
              {formatAmount(props.quote.carrierTotal)}
            </Typography>
          </Grid>
          {/* Row 4: Insurance Rate (Indented) */}
          <Show when={props.isInsuranceRequest}>
            <Grid item size={6}>
              <Box class={cls.indentedLabel}>
                <Typography
                  class={`${cls.tightSpacing} text-wrap`}
                  variant="body2"
                >
                  Insurance Rate
                </Typography>
              </Box>
            </Grid>
            <Grid item size={6}>
              <Typography class="text-wrap!" variant="body2" align="right">
                {formatAmount(props.insuranceQuote?.premium ?? 0)}
              </Typography>
            </Grid>
          </Show>
          {/* Row 5: Customer Cost (Bold) */}
          <Grid item size={6} mt={4}>
            <Typography
              class={`${cls.boldText} ${cls.tightSpacing} text-wrap!`}
              variant="body2"
            >
              Customer
            </Typography>
          </Grid>
          <Grid item size={6} mt={4}>
            <Typography class="text-wrap!" variant="body1" align="right">
              {getTotalPrice(
                props.isInsuranceRequest,
                props.quote.customerTotal ?? 0,
                props.insuranceQuote?.premium ?? 0,
              )}
            </Typography>
          </Grid>
          {/* Row 6: Insurance & Fees (Indented) */}
          <Show when={props.isInsuranceRequest}>
            <Grid item size={6}>
              <Box class={cls.indentedLabel}>
                <Typography
                  class={`${cls.tightSpacing} text-wrap`}
                  variant="body2"
                >
                  Insurance & Fees
                </Typography>
              </Box>
            </Grid>
            <Grid item size={6}>
              <Typography
                class="text-wrap! wrap-break-word!"
                variant="body2"
                align="right"
              >
                {formatAmount(
                  (props.insuranceQuote?.premium ?? 0) +
                    (props.insuranceQuote?.serviceFee ?? 0),
                )}
              </Typography>
            </Grid>
          </Show>
        </Grid>

        <Box>
          <Show when={!props.isHistory}>
            <Grid item size={12}>
              <QuoteActionDetailsLegacy
                isInsuranceRequest={props.isInsuranceRequest}
                quoteParameters={props.quoteParameters}
                quote={props.quote}
                insuranceQuote={props.insuranceQuote}
                customerId={props.customerId}
                testMode={props.testMode}
              />
            </Grid>
          </Show>
        </Box>
      </Box>
    </Box>
  );
};
